/**
 * /queries — Governed Query workspace. Surface Layer (/01)
 *
 * Queries are governed: the browser never issues raw SQL. The operator
 * picks an allowlisted query template, supplies parameters, and dispatches
 * a `queries.run_governed` ActionFrame. Execution only resolves once an
 * approved query connector grant exists for the tenant — until then this
 * surface renders the empty results state (outside-in).
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Column,
  DataTable,
  Grid,
  InlineNotification,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
  Tile,
} from '@carbon/react';
import { Search } from '@carbon/icons-react';
import { useDispatchStore } from '../rebuilt/action-dispatch';
import '../styles/views.scss';

export const Route = createFileRoute('/queries')({
  component: QueriesView,
});

/**
 * Allowlisted query templates. These are display options (a governed
 * catalog), not tenant data — the template id is resolved to a vetted
 * route server-side once a connector grant is present.
 */
const QUERY_TEMPLATES = [
  { id: 'active_projects', label: 'Active projects' },
  { id: 'recent_documents', label: 'Recent documents' },
  { id: 'notification_summary', label: 'Notification summary' },
] as const;

interface ResultRow {
  id: string;
  field: string;
  value: string;
}

const RESULT_HEADERS = [
  { key: 'field', header: 'Field' },
  { key: 'value', header: 'Value' },
];

function QueriesView() {
  const dispatch = useDispatchStore((s) => s.dispatch);
  const [template, setTemplate] = useState<string>(QUERY_TEMPLATES[0].id);
  const [paramOne, setParamOne] = useState('');
  const [paramTwo, setParamTwo] = useState('');
  const [detail, setDetail] = useState<{ status: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // No governed-results hook exists yet; keep results local and render the
  // empty state until a query connector grant resolves the ActionFrame.
  const [rows] = useState<ResultRow[]>([]);

  async function runQuery() {
    setBusy(true);
    const params = {
      limit: paramOne.trim() || undefined,
      filter: paramTwo.trim() || undefined,
    };
    const entry = await dispatch(
      'queries.run_governed',
      { template, params },
      { label: 'Run query' }
    );
    setDetail({ status: entry.status, text: entry.detail });
    setBusy(false);
  }

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Queries</h1>
        <p className="view__subtitle">
          Run governed, allowlisted queries. The browser never issues raw SQL — each run is a
          reviewed ActionFrame against a vetted query route.
        </p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <h2 className="view-tile__value">New query</h2>
              <Select
                id="queries-template"
                labelText="Query template"
                value={template}
                onChange={(event) => setTemplate(event.currentTarget.value)}
              >
                {QUERY_TEMPLATES.map((item) => (
                  <SelectItem key={item.id} value={item.id} text={item.label} />
                ))}
              </Select>
              <TextInput
                id="queries-param-limit"
                labelText="Limit"
                placeholder="e.g. 25"
                value={paramOne}
                onChange={(event) => setParamOne(event.currentTarget.value)}
              />
              <TextInput
                id="queries-param-filter"
                labelText="Filter"
                placeholder="e.g. status:active"
                value={paramTwo}
                onChange={(event) => setParamTwo(event.currentTarget.value)}
              />
              <div className="view-actions">
                <Button renderIcon={Search} disabled={busy} onClick={() => void runQuery()}>
                  Run query
                </Button>
              </div>
              {detail ? (
                <InlineNotification
                  kind={detail.status === 'error' || detail.status === 'denied' ? 'error' : 'info'}
                  lowContrast
                  hideCloseButton
                  title={detail.status === 'error' || detail.status === 'denied' ? 'Dispatch failed' : 'Dispatched'}
                  subtitle={detail.text}
                />
              ) : null}
            </Tile>
          </Column>

          <Column sm={4} md={8} lg={16}>
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title="Governed results (Beta)"
              subtitle="Query results require an approved query connector grant for this workspace. Runs are recorded as ActionFrames until a grant is active."
            />
          </Column>

          {rows.length === 0 ? (
            <Column sm={4} md={8} lg={16}>
              <Tile className="view-empty">
                <h2>No results yet</h2>
                <p className="view-tile__label">
                  Run a governed query above. Results render here once a query connector grant is approved.
                </p>
              </Tile>
            </Column>
          ) : (
            <Column sm={4} md={8} lg={16}>
              <div className="surface-table-scroll">
                <DataTable rows={rows} headers={RESULT_HEADERS}>
                  {({ rows: tableRows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {tableHeaders.map((header) => {
                            const { key, ...headerProps } = getHeaderProps({ header });
                            return (
                              <TableHeader key={key} {...headerProps}>
                                {header.header}
                              </TableHeader>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableRows.map((row) => {
                          const { key, ...rowProps } = getRowProps({ row });
                          return (
                            <TableRow key={key} {...rowProps}>
                              {row.cells.map((cell) => (
                                <TableCell key={cell.id}>{cell.value}</TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </DataTable>
              </div>
            </Column>
          )}
        </Grid>
      </div>
    </div>
  );
}
